// src/components/viewer/ReadOnlyHeader.tsx
import React, { useState } from 'react';
import { Compass, Globe, Share2, PlusCircle, Check, WifiOff, Download } from 'lucide-react';
import { usePwaInstall } from '../../hooks/usePwaInstall';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';

export interface ReadOnlyHeaderProps {
  tripName: string;
  startDate?: number;
  endDate?: number;
  photoCount: number;
  onExitReadOnly: () => void;
  manifestUrl: string;
}

export const ReadOnlyHeader: React.FC<ReadOnlyHeaderProps> = ({
  tripName,
  startDate,
  endDate,
  photoCount,
  onExitReadOnly,
}) => {
  const [copied, setCopied] = useState(false);
  const { isOnline } = useNetworkStatus();
  const { canInstall, promptInstall } = usePwaInstall();

  const formattedDateRange = React.useMemo(() => {
    if (!startDate || !endDate) return null;
    try {
      const d1 = new Date(startDate * 1000).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
      const d2 = new Date(endDate * 1000).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
      const days = Math.round((endDate - startDate) / 86400);
      return `${d1} – ${d2} (${days > 0 ? `${days} giorni` : '1 giorno'})`;
    } catch {
      return null;
    }
  }, [startDate, endDate]);

  const handleCopyLink = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
    } catch {}
  };

  return (
    <header className="min-h-14 pt-[env(safe-area-inset-top,0px)] border-b border-slate-800 bg-slate-900/95 backdrop-blur px-3 sm:px-4 flex items-center justify-between select-none z-40 transition-all">
      {/* Left: Brand Identity & Trip Name */}
      <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
        <div className="w-8 h-8 rounded-lg bg-sky-500/15 border border-sky-500/30 flex items-center justify-center text-sky-400 shrink-0">
          <Compass className="w-5 h-5 animate-[spin_25s_linear_infinite]" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xs sm:text-sm font-bold text-slate-100 truncate">{tripName}</h1>
            {/* Read-Only Badge */}
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30 shrink-0">
              <Globe className="w-3 h-3 text-sky-400" />
              <span>Sola Lettura</span>
            </span>
          </div>
          {formattedDateRange && (
            <p className="text-[10px] sm:text-[11px] text-slate-400 truncate">
              {formattedDateRange} • {photoCount} foto
            </p>
          )}
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
        {!isOnline && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[11px] font-medium animate-pulse">
            <WifiOff className="w-3 h-3 text-amber-400" />
            <span className="hidden xs:inline">Offline</span>
          </div>
        )}

        {/* Share Link Button */}
        <button
          type="button"
          onClick={handleCopyLink}
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white rounded-xl text-xs font-medium border border-slate-700 transition cursor-pointer"
          title="Copia link condivisibile negli appunti"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5 text-sky-400" />}
          <span className="hidden sm:inline">{copied ? 'Copiato!' : 'Condividi'}</span>
        </button>

        {/* PWA Install Button */}
        {canInstall && (
          <button
            type="button"
            onClick={promptInstall}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-sky-600/90 hover:bg-sky-500 text-white rounded-xl text-xs font-medium transition cursor-pointer"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Installa PWA</span>
          </button>
        )}

        {/* CTA: Create Personal Trip */}
        <button
          type="button"
          onClick={onExitReadOnly}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-xs font-semibold shadow-md shadow-blue-500/20 transition cursor-pointer"
          title="Passa alla modalità personale per caricare il tuo viaggio"
        >
          <PlusCircle className="w-3.5 h-3.5" />
          <span className="hidden xs:inline">Crea il tuo Viaggio</span>
        </button>
      </div>
    </header>
  );
};
